import {
  addDays,
  addMinutes,
  addMonths,
  addQuarters,
  addWeeks,
  addYears,
  isAfter,
  isBefore,
  isSameDay,
} from 'date-fns';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: fix according to result of #67489
import type { TaskKindOption } from 'features/programs/form/features/activity-types';
import type { ActivityDataObject, LastActivitySubmission, Log } from 'models/service/activity';

export type Option = {
  id: string;
  logs?: Log[];
  name: string;
};

export type PeriodType = {
  annually: 'y';
  daily: 'd';
  monthly: 'M';
  quarterly: 'Q';
  weekly: 'w';
};

export type Reminder = {
  id?: string;
  minutes: number;
  type?: number;
};

export type Task = {
  id?: string;
  input: string;
  kind?: TaskKindOption;
  logs?: Log[];
  name: string;
  options?: Option[];
};

export type Time = {
  id?: string;
  time: string;
};

export const addPeriodType = (date: Date, periodType: keyof PeriodType, duration: number) => {
  switch (periodType) {
    case 'annually':
      return addYears(date, duration);
    case 'daily':
      return addDays(date, duration);
    case 'monthly':
      return addMonths(date, duration);
    case 'quarterly':
      return addQuarters(date, duration);
    case 'weekly':
      return addWeeks(date, duration);
  }
};

export type Activity = {
  description: string;
  endTime: Date;
  htmlDescription: string;
  id: string;
  isWithings: boolean;
  lastActivitySubmission?: LastActivitySubmission;
  multipleSubmissions: boolean;
  name: string;
  nextPeriodDate: Date | null;
  periodicityType?: PeriodicityType;
  programsTopicId: string;
  reminders?: Reminder[];
  startTime: Date;
  tasks: Task[];
  times: Time[];
};

export type PeriodicityType = 'one_time' | keyof PeriodType;

// eslint-disable-next-line sonarjs/cognitive-complexity -- Leave it here fow now
export const getNextPeriodDate = (startTime: Date, endTime: Date, periodicityType?: PeriodicityType) => {
  const now = new Date();

  if (isAfter(now, endTime)) {
    return null;
  }

  if (isSameDay(startTime, now)) {
    return startTime;
  }

  let nextPeriodDate = startTime;

  switch (periodicityType) {
    case 'annually':
      while (isBefore(nextPeriodDate, now) && !isSameDay(nextPeriodDate, now)) {
        nextPeriodDate = addYears(nextPeriodDate, 1);
      }

      break;
    case 'daily':
    case 'one_time':
      return now;
    case 'monthly':
      while (isBefore(nextPeriodDate, now) && !isSameDay(nextPeriodDate, now)) {
        nextPeriodDate = addMonths(nextPeriodDate, 1);
      }

      break;
    case 'quarterly':
      while (isBefore(nextPeriodDate, now) && !isSameDay(nextPeriodDate, now)) {
        nextPeriodDate = addQuarters(nextPeriodDate, 1);
      }

      break;
    case 'weekly':
      while (isBefore(nextPeriodDate, now) && !isSameDay(nextPeriodDate, now)) {
        nextPeriodDate = addWeeks(nextPeriodDate, 1);
      }

      break;
    case undefined:
      throw new Error('Not implemented yet: undefined case');
  }

  if (isAfter(nextPeriodDate, endTime)) {
    return null;
  }

  return nextPeriodDate;
};

export const createActivity = (data: ActivityDataObject): Activity => {
  const startTime = data.start_time ? new Date(data.start_time) : addMinutes(new Date(), 10);
  const endTime = data.end_time ? new Date(data.end_time) : addMinutes(startTime, 60);

  return {
    description: data.description ?? '',
    endTime,
    htmlDescription: data.html_description ?? '',
    id: data.id ?? '',
    isWithings: Boolean(data.is_withings),
    lastActivitySubmission: data.last_activity_submission,
    multipleSubmissions: data.multiple_submissions ?? false,
    name: data.name ?? '',
    nextPeriodDate: getNextPeriodDate(startTime, endTime, data.periodicity_type),
    periodicityType: data.periodicity_type,
    programsTopicId: data.programs_topic?.id ?? '',
    reminders: data.reminders,
    startTime,
    tasks: data.tasks ?? [],
    times: data.times ?? [],
  };
};

export const isBloodPressureType = (activity: Activity) =>
  activity.tasks.some((task) => task.kind === 'withings_blood_pressure_diastolic');

export const isWithingsWeightType = (activity: Activity) =>
  activity.tasks.some((task) => task.kind === 'withings_weight');
